/**
 * Modal
 *
 * To open a modal simply dispatch a openModal action to the redux store. For example:
 * 	dispatch(openModal('modalName', { foo: 'bar' }))
 *
 * The openModal action takes two parameters: name and data. The first parameter (name)
 * is used to control which modal should open. The second is used to pass data into the
 * modal.
 *
 * Create a new modal type in the components directory, import it and include it in the
 * switch inside the getContent method.
 *
 * @author Daniel Kvistgaard <dkvistgaard@vertic.com>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactModal from 'react-modal';

import { closeModal, openModal } from '../../store/actions/modal';

import './modal.scss';

const Base = React.lazy(() => { return import('./components/base'); });
const Video = React.lazy(() => { return import('./components/video'); });
const Calendar = React.lazy(() => { return import('./components/calendar'); });
const Form = React.lazy(() => { return import('./components/form'); });
const Login = React.lazy(() => { return import('./components/login'); });

ReactModal.setAppElement('#main');

class Modal extends React.Component {
	componentDidMount() {
		const hashtag = window.location.hash;
		this.registerListeners();

		if (hashtag === '#login') {
			const { dispatch } = this.props;
			dispatch(openModal('login', {}));
		}

		this.listenForDomChanges();
	}

	listenForDomChanges = () => {
		const targetNode = document.getElementsByClassName('C2-search-results__wrapper')[0];

		if (targetNode) {
			const config = {
				attributes: true,
				subtree: true,
				childList: true,
			};

			const observer = new MutationObserver(this.registerModalLoginListeners);

			observer.observe(targetNode, config);
		}
	}

	registerModalLoginListeners = () => {
		const modalLoginButtons = document.querySelectorAll('.modal-popup-link');

		if (modalLoginButtons.length) {
			Array.from(modalLoginButtons).forEach((button) => {
				button.removeEventListener('click', this.logInOpen);
				button.addEventListener('click', this.onLoginOpen);
			});
		}
	}

	registerListeners = () => {
		const calendarButtons = document.querySelectorAll('.js-calendar-btn');
		const formButtons = document.querySelectorAll('.js-form-btn');
		const popupButtons = document.querySelectorAll('.js-popup-btn');
		const loginButtons = document.querySelectorAll('.js-login-btn');

		if (calendarButtons) {
			Array.from(calendarButtons).forEach((calendarButton) => {
				calendarButton.addEventListener('click', this.onCalendarOpen);
			});
		}

		if (formButtons) {
			Array.from(formButtons).forEach((formButton) => {
				formButton.addEventListener('click', this.onFormOpen);
			});
		}

		if (popupButtons) {
			Array.from(popupButtons).forEach((popupButton) => {
				popupButton.addEventListener('click', this.onPopupOpen);
			});
		}

		if (loginButtons.length) {
			Array.from(loginButtons).forEach((button) => {
				button.addEventListener('click', this.onLoginOpen);
			});
		}

		this.registerModalLoginListeners();
	}

	onPopupOpen = (event) => {
		event.preventDefault();
		const { dispatch } = this.props;
		const {
			title,
			description,
			ctaPrimaryLabel,
			ctaPrimaryLink,
			ctaSecondaryLabel,
			ctaSecondaryLink,
		} = event.target.dataset;

		dispatch(openModal('base', {
			title,
			description,
			ctaPrimaryLabel,
			ctaPrimaryLink,
			ctaSecondaryLabel,
			ctaSecondaryLink,
		}));
	}

	onCalendarOpen = (event) => {
		event.preventDefault();
		const { dispatch } = this.props;
		const {
			googleCalendarUrl,
			googleCalendarIcon,
			googleCalendarLabel,
			outlookCalendarUrl,
			outlookCalendarIcon,
			outlookCalendarLabel,
			appleCalendarUrl,
			appleCalendarIcon,
			appleCalendarLabel,
			title,
			copy,
			buttonLabel,
		} = event.target.dataset;

		dispatch(openModal('calendar', {
			googleCalendarUrl,
			googleCalendarIcon,
			googleCalendarLabel,
			outlookCalendarUrl,
			outlookCalendarIcon,
			outlookCalendarLabel,
			appleCalendarUrl,
			appleCalendarIcon,
			appleCalendarLabel,
			title,
			copy,
			buttonLabel,
		}));
	}

	onFormOpen = (event) => {
		event.preventDefault();
		const { dispatch } = this.props;
		const { formModalData } = event.target.dataset;
		dispatch(openModal('form', { formModalData }));
	}

	onLoginOpen = (event) => {
		event.preventDefault();
		const { dispatch } = this.props;
		dispatch(openModal('login', {}));
	}


	onRequestClose = () => {
		const { dispatch } = this.props;

		dispatch(closeModal());
	}

	onCloseClick = (event) => {
		event.preventDefault();

		this.onRequestClose();
	}

	getContent = () => {
		const { modal, data } = this.props;
		const { loginModal, formModal } = data;
		const props = {
			...modal.data,
			onRequestClose: this.onRequestClose,
			onCloseClick: this.onCloseClick,
		};

		switch (modal.name) {
			case 'video':
				return <Video {...props} />;

			case 'calendar':
				return <Calendar {...props} />;

			case 'form':
				return <Form data={{ ...formModal }} {...props} />;

			case 'login':
				return <Login data={{ ...loginModal }} {...props} />;
			default:
				return <Base {...props} />;
		}
	}

	render() {
		const { modal } = this.props;
		const isOpen = !!modal.name;

		let className = 'modal__content';
		if (modal.name) className += ` modal__content--${modal.name}`;

		return (
			<ReactModal
				portalClassName="modal"
				overlayClassName="modal__overlay"
				className={className}
				bodyOpenClassName="modal-body--open"

				isOpen={isOpen}

				onRequestClose={this.onRequestClose}
			>
				{isOpen ? (
					<React.Suspense fallback={<p>Loading...</p>}>
						{this.getContent()}
					</React.Suspense>
				) : ''}
			</ReactModal>
		);
	}
}

Modal.propTypes = {
	modal: PropTypes.shape({}).isRequired,
	dispatch: PropTypes.func.isRequired,
	data: PropTypes.shape({}).isRequired,
};

export default connect((state) => {
	return {
		modal: state.modal,
	};
})(Modal);
